/**
 * Wave 351 · Brain trace schemas — trace + span observations for autonomy debugging.
 */

import { z } from 'zod';
import { isoDateTimeSchema } from './common.js';

/**
 * Wave 351 fix · brain DTO ids cover both kebab-case capability ids
 * (`po-proposer`) and UUIDs (`crypto.randomUUID()` — 36 chars with
 * hyphens). The strict idSchema in common.ts requires `[a-z0-9]{24,32}`
 * which rejects both, so brain DTOs use this relaxed schema.
 */
const idSchema = z.string().min(1).max(128);
import { CapabilityOwnerEnum } from './brain-capability.js';

// ═══════════════════════════════════════════════════════════════════════════════
// Enums
// ═══════════════════════════════════════════════════════════════════════════════

export const SpanTypeEnum = z.enum([
  'housy_turn',
  'llm_call',
  'tool_call',
  'prumo_handoff',
  'proposal_created',
  'approval_decision',
  'executor_run',
  'guardrail',
  'self_heal',
  'connector_probe',
  'eval_run',
  'system_event',
]);
export type SpanType = z.infer<typeof SpanTypeEnum>;

/**
 * Wave 351 fix · trace.service.ts emits `ok | failed | skipped | degraded`.
 * `running | partial` are reserved for streamed traces (future); included
 * so the schema accepts both producers.
 */
export const TraceStatusEnum = z.enum([
  'ok', 'failed', 'skipped', 'degraded', 'running', 'partial',
]);
export type TraceStatus = z.infer<typeof TraceStatusEnum>;

/**
 * Wave 351 fix · spans share the trace TraceStatus union. `redacted`
 * is added because the API may strip a span's body when it contains
 * unredactable secrets.
 */
export const SpanStatusEnum = z.enum([
  'ok', 'failed', 'skipped', 'degraded', 'redacted',
]);
export type SpanStatus = z.infer<typeof SpanStatusEnum>;

// ═══════════════════════════════════════════════════════════════════════════════
// Trace List Item
// ═══════════════════════════════════════════════════════════════════════════════

export const BrainTraceListItemDTOSchema = z.object({
  id: idSchema,
  workflow: z.string().describe('Workflow name (e.g., "autonomy_proposal", "daily_digest")'),
  capabilityId: idSchema.nullable(),
  owner: CapabilityOwnerEnum,
  triggeredBy: z.string().describe('Event or trigger name'),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
  status: TraceStatusEnum,
  totalSpans: z.number().int().min(0),
  errorSpans: z.number().int().min(0),
  contextRef: z.string().nullable().describe('Reference to related object (e.g., proposal id)'),
});
export type BrainTraceListItemDTO = z.infer<typeof BrainTraceListItemDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Trace List Wrapper (route response)
// ═══════════════════════════════════════════════════════════════════════════════

export const BrainTraceListDTOSchema = z.object({
  traces: z.array(BrainTraceListItemDTOSchema),
  generatedAt: isoDateTimeSchema,
  _degraded: z.boolean().optional(),
  _reason: z.string().optional(),
});
export type BrainTraceListDTO = z.infer<typeof BrainTraceListDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Trace Span
// ═══════════════════════════════════════════════════════════════════════════════

export const BrainTraceSpanDTOSchema = z.object({
  id: idSchema,
  parentSpanId: idSchema.nullable(),
  type: SpanTypeEnum,
  name: z.string(),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
  status: SpanStatusEnum,
  latencyMs: z.number().int().min(0).nullable(),
  inputSummary: z.string().nullable().describe('Truncated input for debugging'),
  outputSummary: z.string().nullable().describe('Truncated output/result'),
  errorMessage: z.string().nullable(),
  durationMs: z.number().int().min(0).nullable(),
});
export type BrainTraceSpanDTO = z.infer<typeof BrainTraceSpanDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Trace Detail (full trace + spans)
// ═══════════════════════════════════════════════════════════════════════════════

export const BrainTraceDetailDTOSchema = z.object({
  trace: z.object({
    id: idSchema,
    workflow: z.string(),
    capabilityId: idSchema.nullable(),
    owner: CapabilityOwnerEnum,
    status: TraceStatusEnum,
    latencyMs: z.number().int().min(0).nullable(),
    failureReason: z.string().nullable(),
    inputSummary: z.string().nullable(),
    outputSummary: z.string().nullable(),
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
  }),
  spans: z.array(BrainTraceSpanDTOSchema),
});
export type BrainTraceDetailDTO = z.infer<typeof BrainTraceDetailDTOSchema>;
